import { Shimmer } from "@/components/ai-elements/shimmer";
import DialogViewPrompt from "@/components/prompt/dialog-view-prompt";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast, useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useSavedProposedTactic } from "@/contexts/saved-proposed-tactics-context";
import { useTeams } from "@/contexts/teams-provider";
import { TeamUserRolesEnum } from "@/enums/team-user-role";
import { useTranslation } from "@/hooks/useTranslation";
import { getApiErrorCode } from "@/lib/api-error";
import {
  isProposedTactic,
  readRevisedToolCallIds,
} from "@/lib/proposed-tactic";
import {
  PROMPT_CATEGORY_IDS,
  PROPOSED_TACTIC_MAX_CHARS,
  ProposedTactic,
  PromptCategoryId,
  PromptDTO,
} from "@shaarp/common";
import { ToolUIPart } from "ai";
import { CrosshairIcon, Loader2Icon } from "lucide-react";
import { useId, useState } from "react";

type ChatToolProposeTacticProps = {
  readonly part: ToolUIPart;
  readonly live: boolean;
};

/**
 * A tactic the assistant wrote from the conversation, for its author to read,
 * correct and save.
 *
 * Nothing exists until the save click: the model has no way to write it, so a
 * page it scraped cannot plant an instruction in the author's library. What is
 * saved is what is on screen, edits included, and it is always personal — the
 * handover to the team is a second, separate button shown only to those who may.
 *
 * Saved once, it stays saved: a reopened conversation asks which tactic came
 * from this call and shows that one, never the form a second time.
 */
export default function ChatToolProposeTactic({
  part,
  live,
}: ChatToolProposeTacticProps) {
  const { t } = useTranslation();
  const api = useApi();
  const toastError = useToastError();
  const { teamSelected, isManager, isGod } = useTeams();

  const proposal =
    part.state === "output-available" && isProposedTactic(part.output)
      ? part.output
      : null;
  // A revision, asked for by the author: saving it updates the tactic saved
  // from an earlier version rather than adding one next to it.
  const revisesToolCallIds = proposal ? readRevisedToolCallIds(part.output) : [];

  // The author's edits only, not a copy of the proposal: live, the card mounts
  // before the output exists, and a state seeded then would stay empty.
  const [edited, setEdited] = useState<ProposedTactic | null>(null);
  // Off until the author switches it on, as in the tactic library's create
  // form (`docs/decisions/0019`).
  const [isAutoEnabled, setIsAutoEnabled] = useState<boolean>(false);
  const autoUseId = useId();
  const autoUseHelperId = useId();
  const [savedHere, setSavedHere] = useState<PromptDTO | null>(null);
  const [isBusy, setIsBusy] = useState<boolean>(false);
  const [isConfirmingShare, setIsConfirmingShare] = useState<boolean>(false);
  const [isViewing, setIsViewing] = useState<boolean>(false);

  // Asked by the thread once for all its proposals, not by each card.
  const stored = useSavedProposedTactic(part.toolCallId);
  // Once revised, the thread's answer is the truth: saving the revision moved
  // the tactic off this call, whatever this card saved before.
  const saved = stored.isRevised
    ? stored.saved
    : (savedHere ?? stored.saved);
  const isShared = saved?.ownerType === "team";

  // The model writes the tactic, or its changes, as the call's input, so the
  // wait is long enough to need a sign of life. Outside the live stream a pending part is
  // dead — nothing will complete it — and draws nothing, like `output-error`.
  const isDrafting =
    live &&
    (part.state === "input-streaming" || part.state === "input-available");
  if (isDrafting) {
    return (
      <div className="my-2 flex w-full items-center gap-2 rounded-xl border bg-background p-3 not-prose">
        <Loader2Icon className="size-4 shrink-0 animate-spin text-primary" />
        <Shimmer as="span" className="text-sm font-medium">
          {t("chat.message.tool.proposeTactic.drafting")}
        </Shimmer>
      </div>
    );
  }

  // Outside the live stream, nothing until the answer lands, rather than a
  // form that may turn into a confirmation under the author's cursor — unless
  // this card saved it, which the answer can only confirm. A live proposal has
  // not been saved yet, so its form shows at once. A failed lookup falls back
  // to the form, whose save returns the existing tactic, not a copy.
  const draft = edited ?? proposal;
  if (!draft || (!live && stored.isLookingUp && !savedHere)) {
    return null;
  }

  const update = (patch: Partial<ProposedTactic>) =>
    setEdited((current) => ({ ...(current ?? draft), ...patch }));

  const isComplete =
    draft.title.trim() !== "" &&
    draft.description.trim() !== "" &&
    draft.content.trim() !== "";

  const onSave = async () => {
    if (isBusy || !isComplete) {
      return;
    }
    try {
      setIsBusy(true);
      const { data } = await api.post<PromptDTO>("/prompt/from-conversation", {
        // The tactic's own fields only: the output also names the calls a
        // revision replaces, which travel beside it.
        prompt: {
          title: draft.title,
          description: draft.description,
          content: draft.content,
          categoryId: draft.categoryId,
          isAutoEnabled,
        },
        toolCallId: part.toolCallId,
        ...(revisesToolCallIds.length > 0 && { revisesToolCallIds }),
      });
      setSavedHere(data);
      stored.markSaved(data, revisesToolCallIds);
      toast({ title: t("chat.message.tool.proposeTactic.savedToast") });
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === "TACTIC_ALREADY_SHARED") {
        toastError({
          description: t("chat.message.tool.proposeTactic.alreadyShared"),
        });
      } else if (code === "REVISED_TACTIC_ALREADY_SHARED") {
        toastError({
          description: t("chat.message.tool.proposeTactic.revisedShared"),
        });
      } else {
        toastError();
      }
    } finally {
      setIsBusy(false);
    }
  };

  const onShare = async () => {
    if (isBusy || !saved) {
      return;
    }
    try {
      setIsBusy(true);
      const { data } = await api.post<PromptDTO>(
        `/prompt/${saved.id}/share-to-team`,
      );
      setSavedHere(data);
      stored.markSaved(data);
      toast({ title: t("chat.message.tool.proposeTactic.sharedToast") });
    } catch {
      toastError();
    } finally {
      setIsBusy(false);
      setIsConfirmingShare(false);
    }
  };

  // The role of the team the request will carry, as the API checks it: the
  // context's `isAdmin` is true for an admin of any team, which would show a
  // button the API then refuses.
  const canShare =
    isManager || isGod || teamSelected?.userRole === TeamUserRolesEnum.ADMIN;

  return (
    <div className="my-2 flex w-full flex-col gap-3 rounded-xl border bg-background p-4 not-prose">
      <div className="flex items-center gap-2">
        <CrosshairIcon className="size-4 shrink-0 text-primary" />
        <span className="text-sm font-medium">
          {t("chat.message.tool.proposeTactic.heading")}
        </span>
      </div>

      {saved ? (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-muted-foreground">
            {isShared
              ? t("chat.message.tool.proposeTactic.sharedBody", {
                  title: saved.title,
                })
              : t(
                  saved.isAutoEnabled
                    ? "chat.message.tool.proposeTactic.savedBody"
                    : "chat.message.tool.proposeTactic.savedBodyManual",
                  { title: saved.title },
                )}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsViewing(true)}
            >
              {t("chat.message.tool.proposeTactic.view")}
            </Button>
            {canShare && !isShared && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isBusy}
                onClick={() => setIsConfirmingShare(true)}
              >
                {t("chat.message.tool.proposeTactic.share")}
              </Button>
            )}
          </div>
        </div>
      ) : stored.isRevised ? (
        <p className="text-sm text-muted-foreground">
          {t("chat.message.tool.proposeTactic.replacedBody")}
        </p>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            {t(
              revisesToolCallIds.length > 0
                ? "chat.message.tool.proposeTactic.reviewHintRevision"
                : "chat.message.tool.proposeTactic.reviewHint",
            )}
          </p>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="propose-tactic-title">
              {t("chat.message.tool.proposeTactic.titleLabel")}
            </Label>
            <Input
              id="propose-tactic-title"
              value={draft.title}
              maxLength={PROPOSED_TACTIC_MAX_CHARS.title}
              onChange={(event) => update({ title: event.target.value })}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="propose-tactic-description">
              {t("chat.message.tool.proposeTactic.descriptionLabel")}
            </Label>
            <Textarea
              id="propose-tactic-description"
              rows={2}
              value={draft.description}
              maxLength={PROPOSED_TACTIC_MAX_CHARS.description}
              onChange={(event) => update({ description: event.target.value })}
            />
            <span className="text-xs text-muted-foreground">
              {t("chat.message.tool.proposeTactic.descriptionHint")}
            </span>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="propose-tactic-content">
              {t("chat.message.tool.proposeTactic.contentLabel")}
            </Label>
            <Textarea
              id="propose-tactic-content"
              rows={8}
              value={draft.content}
              maxLength={PROPOSED_TACTIC_MAX_CHARS.content}
              onChange={(event) => update({ content: event.target.value })}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>{t("chat.message.tool.proposeTactic.categoryLabel")}</Label>
            <Select
              value={draft.categoryId}
              onValueChange={(value) =>
                update({ categoryId: value as PromptCategoryId })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PROMPT_CATEGORY_IDS.map((id) => (
                  <SelectItem key={id} value={id}>
                    {t(`prompt.categories.${id}.name`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <Label htmlFor={autoUseId}>
                {t("prompt.create.fields.autoUse.label")}
              </Label>
              <p id={autoUseHelperId} className="text-xs text-gray-500">
                {t("prompt.create.fields.autoUse.helper")}
              </p>
            </div>
            <Switch
              id={autoUseId}
              checked={isAutoEnabled}
              onCheckedChange={setIsAutoEnabled}
              aria-describedby={autoUseHelperId}
            />
          </div>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              size="sm"
              disabled={isBusy || !isComplete}
              onClick={onSave}
            >
              {isBusy && <Loader2Icon className="size-4 animate-spin" />}
              {t("chat.message.tool.proposeTactic.save")}
            </Button>
            <span className="text-xs text-muted-foreground">
              {t("chat.message.tool.proposeTactic.personalNote")}
            </span>
          </div>
        </>
      )}

      <AlertDialog
        open={isConfirmingShare}
        onOpenChange={setIsConfirmingShare}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("chat.message.tool.proposeTactic.shareConfirmTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                saved?.isAutoEnabled
                  ? "chat.message.tool.proposeTactic.shareConfirmBodyAuto"
                  : "chat.message.tool.proposeTactic.shareConfirmBodyManual",
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("chat.message.tool.proposeTactic.shareCancel")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={onShare} disabled={isBusy}>
              {t("chat.message.tool.proposeTactic.shareConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {saved && isViewing && (
        <DialogViewPrompt prompt={saved} onClose={() => setIsViewing(false)} />
      )}
    </div>
  );
}
